import { Button } from "@/components/ui/button";
import { MarkdownEditor } from "@/components/ui/markdown-editor";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { useDraft } from "@/lib/use-draft";
import { Link, createFileRoute, notFound, useNavigate } from "@tanstack/react-router";

/**
 * `/projects/:slug/wiki/:wikiSlug/edit` — edit or delete a wiki page.
 * Changes land in Hub and are pushed to the project's GitLab wiki.
 */

type WikiPage = { slug: string; title: string; content: string };

type WikiPageResponse = {
  page: WikiPage;
  canEdit: boolean;
};

export const Route = createFileRoute("/_app/projects/$slug/wiki/$wikiSlug/edit")({
  loader: async ({ params }) => {
    try {
      return await api.get<WikiPageResponse>(
        `/api/projects/${params.slug}/wiki/${encodeURIComponent(params.wikiSlug)}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: WikiEditPage,
});

function WikiEditPage() {
  const { page } = Route.useLoaderData();
  const { slug, wikiSlug } = Route.useParams();
  const navigate = useNavigate();

  const [title, setTitle, clearTitle] = useDraft(`wiki-edit:${slug}:${wikiSlug}:title`, page.title);
  const [content, setContent, clearContent] = useDraft(
    `wiki-edit:${slug}:${wikiSlug}:content`,
    page.content,
  );
  const savePage = useApiMutation(
    (body: { title: string; content: string }) =>
      api.patch<{ slug: string; title: string }>(
        `/api/projects/${slug}/wiki/${encodeURIComponent(wikiSlug)}`,
        body,
      ),
    {
      onSuccess: (updated) => {
        clearTitle();
        clearContent();
        return navigate({
          to: "/projects/$slug/wiki/$wikiSlug",
          params: { slug, wikiSlug: updated.slug },
        });
      },
      errorMessage: (err) =>
        err instanceof ApiError
          ? "Couldn't save changes — you may not have permission."
          : "Couldn't save changes.",
    },
  );
  const removePage = useApiMutation(
    () => api.delete(`/api/projects/${slug}/wiki/${encodeURIComponent(wikiSlug)}`),
    {
      onSuccess: () => navigate({ to: "/projects/$slug/wiki", params: { slug } }),
      errorMessage: (err) =>
        err instanceof ApiError
          ? "Couldn't delete the page — you may not have permission."
          : "Couldn't delete the page.",
    },
  );
  const error = savePage.error ?? removePage.error;

  async function updatePage(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    await savePage.mutate({ title: title.trim(), content });
  }

  async function deletePage() {
    if (!confirm("Delete this wiki page? This cannot be undone.")) return;
    await removePage.mutate();
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-bold">Edit: {page.title}</h2>
        <Button variant="outline" size="sm" asChild>
          <Link to="/projects/$slug/wiki/$wikiSlug" params={{ slug, wikiSlug }}>
            Cancel
          </Link>
        </Button>
      </div>

      <div className="rounded-md border bg-card p-6">
        <p className="text-xs text-muted-foreground mb-4">
          Saved in Hub — synced to the project's GitLab wiki too.
        </p>
        <form onSubmit={updatePage} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="title" className="text-sm font-medium leading-none">
              Title <span className="text-destructive">*</span>
            </label>
            <input
              id="title"
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          <div className="space-y-2">
            <span className="text-sm font-medium leading-none block">Content</span>
            <MarkdownEditor
              value={content}
              onChange={setContent}
              rows={20}
              uploadEndpoint={`/api/projects/${slug}/uploads`}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex gap-2">
            <Button type="submit" disabled={savePage.pending || !title.trim()}>
              {savePage.pending ? "Saving…" : "Save Changes"}
            </Button>
            <Button variant="outline" type="button" asChild>
              <Link to="/projects/$slug/wiki/$wikiSlug" params={{ slug, wikiSlug }}>
                Cancel
              </Link>
            </Button>
          </div>
        </form>
      </div>

      <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 p-4">
        <h3 className="font-heading text-sm font-semibold text-destructive mb-2">Danger zone</h3>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          disabled={removePage.pending}
          onClick={deletePage}
        >
          {removePage.pending ? "Deleting…" : "Delete page"}
        </Button>
      </div>
    </div>
  );
}
